import { Module, MutationTree, ActionTree, GetterTree } from "vuex"
import axios from "axios"
import { Token } from "@/types/moj-elektro"
import { RootState } from "@/store"

export interface ExternalTokensState {
    tokens: Token[]
    isLoading: boolean
    selectedToken: Token | undefined
    showAddModal: boolean
    token: string
    errorMessage: string | null
}

const state: ExternalTokensState = {
    tokens: [],
    isLoading: false,
    selectedToken: undefined,
    showAddModal: false,
    token: "",
    errorMessage: null,
}

const mutations: MutationTree<ExternalTokensState> = {
    setTokens(state, tokens: Token[]) {
        state.tokens = tokens
    },
    setSelectedToken(state, token: Token | undefined) {
        state.selectedToken = token
    },
    setLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
    },
    addToken(state, token: Token) {
        const existingToken = state.tokens.find((t) => t.id === token.id)

        if (!existingToken) {
            token.token = token.token.slice(0, 5) + "*".repeat(token.token.length - 5)
            state.tokens.push(token)
            state.selectedToken = token
        }
    },
    deleteToken(state, tokenId: number) {
        state.tokens = state.tokens.filter((token) => token.id !== tokenId)
        state.selectedToken = undefined
    },
    setShowAddModal(state, show: boolean) {
        state.showAddModal = show
    },
    setToken(state, value: string) {
        state.token = value
    },
    setErrorMessage(state, value: string | null) {
        state.errorMessage = value
    },
}

const actions: ActionTree<ExternalTokensState, RootState> = {
    async fetchTokens({ commit, dispatch }, errorMessageText: string) {
        commit("setLoading", true)

        try {
            const response = await axios.get("/auth/user/external-credentials")

            const validResponse = response.data.credential ?? []

            commit("setTokens", validResponse)

            if (validResponse.length === 1) {
                commit("setSelectedToken", validResponse[0])
            }
        } catch (error) {
            console.error(errorMessageText, error)

            dispatch(
                "toastMessageModule/showMessage",
                { message: errorMessageText, color: "red" },
                { root: true },
            )
        } finally {
            commit("setLoading", false)
        }
    },
    async addToken({ commit, dispatch }, errorMessageText: string) {
        if (!state.token) {
            return
        }

        commit("setLoading", true)

        try {
            const response = await axios.post("/auth/user/external-credential", {
                data: { token: state.token.trim() },
            })

            commit("addToken", response.data.credential)
            commit("setErrorMessage", null)
        } catch (error: any) {
            console.error(errorMessageText, error)

            commit("setErrorMessage", error?.message)

            dispatch(
                "toastMessageModule/showMessage",
                { message: errorMessageText, color: "red" },
                { root: true },
            )
        } finally {
            commit("setLoading", false)
        }
    },
    async deleteToken(
        { commit, dispatch },
        payload: { tokenId: number; errorMessageText: string },
    ) {
        const { tokenId, errorMessageText } = payload

        commit("setLoading", true)

        try {
            await axios.delete(`/auth/user/external-credential/${tokenId}`)

            dispatch("externalDevices/deleteDevicesByToken", tokenId, { root: true })

            commit("deleteToken", tokenId)
        } catch (error) {
            console.error(errorMessageText, error)

            dispatch(
                "toastMessageModule/showMessage",
                { message: errorMessageText, color: "red" },
                { root: true },
            )
        } finally {
            commit("setLoading", false)
        }
    },
    selectToken({ commit, state }, tokenId: number | undefined) {
        const selectedToken = state.tokens.find((token) => token.id === tokenId)

        commit("setSelectedToken", selectedToken)
    },
    setShowAddModal({ commit }, show: boolean) {
        commit("setShowAddModal", show)
    },
    setToken({ commit }, value: string) {
        commit("setToken", value)
    },
    setErrorMessage({ commit }, value: string | null) {
        commit("setErrorMessage", value)
    },
}

const getters: GetterTree<ExternalTokensState, RootState> = {
    getAllTokens: (state): Token[] => state.tokens,
    getSelectedToken: (state): Token | undefined => state.selectedToken,
    getIsLoading: (state): boolean => state.isLoading,
    getShowAddModal: (state): boolean => state.showAddModal,
    getToken: (state): string => state.token,
    getErrorMessage: (state): string | null => state.errorMessage,
}

const externalTokensModule: Module<ExternalTokensState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default externalTokensModule
